import { useCallback, useEffect, useRef, useState } from "react";
import VideoPanel from "./VideoPanel";
import ControlBar from "./ControlBar";
import TopBar from "./TopBar";
import ExportModal from "./ExportModal";
import { toast } from "sonner";

const FPS = 30;
const FRAME_STEP = 1 / FPS;

export default function VideoCompareApp({ onShowAnalytics }) {
  // Layout: 'horizontal' (side-by-side) or 'vertical' (stacked)
  const [layout, setLayout] = useState("horizontal");

  // Sync: when true, global controls drive both videos together
  const [syncEnabled, setSyncEnabled] = useState(true);

  // Global playback rate (applied when sync is on)
  const [rate, setRate] = useState(1);

  // Sources: { type: 'file'|'url'|'youtube'|null, url, name }
  const [sourceA, setSourceA] = useState(null);
  const [sourceB, setSourceB] = useState(null);

  // Global play state (for sync mode visual only)
  const [globalPlaying, setGlobalPlaying] = useState(false);

  // Refs to each VideoPanel's imperative handle
  const panelARef = useRef(null);
  const panelBRef = useRef(null);
  // Suppresses onLocalPlayChange cascade during coordinated global ops
  const suppressLocalEventsRef = useRef(false);

  const getPanels = useCallback(() => {
    return [panelARef.current, panelBRef.current].filter(Boolean);
  }, []);

  const playAll = useCallback(() => {
    suppressLocalEventsRef.current = true;
    try {
      // Both play() calls in the same synchronous tick — identical to seekTo mirroring.
      getPanels().forEach((p) => p.play());
    } finally {
      suppressLocalEventsRef.current = false;
    }
    setGlobalPlaying(true);
  }, [getPanels]);

  const pauseAll = useCallback(() => {
    suppressLocalEventsRef.current = true;
    try {
      getPanels().forEach((p) => p.pause());
    } finally {
      suppressLocalEventsRef.current = false;
    }
    setGlobalPlaying(false);
  }, [getPanels]);

  const toggleGlobalPlay = useCallback(() => {
    if (globalPlaying) pauseAll();
    else playAll();
  }, [globalPlaying, playAll, pauseAll]);

  const stepFrames = useCallback(
    (direction) => {
      // direction: -1 backward, +1 forward
      getPanels().forEach((p) => p.stepFrame(direction * FRAME_STEP));
    },
    [getPanels]
  );

  const resetAll = useCallback(() => {
    getPanels().forEach((p) => p.seekTo(0));
    setGlobalPlaying(false);
  }, [getPanels]);

  const applyRate = useCallback(
    (newRate) => {
      setRate(newRate);
      if (syncEnabled) {
        getPanels().forEach((p) => p.setRate(newRate));
      }
    },
    [syncEnabled, getPanels]
  );

  // On iOS / iPadOS: auto-switch layout when the device rotates
  useEffect(() => {
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    if (!isIOS) return;

    const mq = window.matchMedia("(orientation: landscape)");

    const apply = (e) => {
      setLayout(e.matches ? "horizontal" : "vertical");
    };

    // Set immediately for current orientation
    apply(mq);

    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // When sync toggled on, propagate current global rate
  useEffect(() => {
    if (syncEnabled) {
      getPanels().forEach((p) => p.setRate(rate));
    }
  }, [syncEnabled, rate, getPanels]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.code === "Space") {
        e.preventDefault();
        toggleGlobalPlay();
      } else if (e.code === "ArrowLeft") {
        e.preventDefault();
        stepFrames(-1);
      } else if (e.code === "ArrowRight") {
        e.preventDefault();
        stepFrames(1);
      } else if (e.key === "r" || e.key === "R") {
        resetAll();
      } else if (e.key === "l" || e.key === "L") {
        setLayout((l) => (l === "horizontal" ? "vertical" : "horizontal"));
      } else if (e.key === "s" || e.key === "S") {
        setSyncEnabled((s) => !s);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggleGlobalPlay, stepFrames, resetAll]);

  const onLocalPlayChange = useCallback(
    (which, isPlaying) => {
      if (!syncEnabled || suppressLocalEventsRef.current) return;
      // Mirror instantly to the other panel — same pattern as onLocalSeek / seekTo.
      const other = which === "A" ? panelBRef.current : panelARef.current;
      suppressLocalEventsRef.current = true;
      try {
        if (other) {
          if (isPlaying) other.play();
          else other.pause();
        }
      } finally {
        suppressLocalEventsRef.current = false;
      }
      setGlobalPlaying(isPlaying);
    },
    [syncEnabled]
  );

  const onLocalSeek = useCallback(
    (which, time) => {
      if (!syncEnabled) return;
      const other = which === "A" ? panelBRef.current : panelARef.current;
      if (other) other.seekTo(time);
    },
    [syncEnabled]
  );

  const [isRecording, setIsRecording] = useState(false);

  const startExport = useCallback(() => setIsRecording(true), []);
  const stopExport  = useCallback(() => setIsRecording(false), []);

  const swapVideos = useCallback(() => {
    setSourceA(sourceB);
    setSourceB(sourceA);
    toast("Videos swapped");
  }, [sourceA, sourceB]);

  const videoWrapperClass =
    layout === "horizontal"
      ? "grid grid-cols-1 md:grid-cols-2 gap-px flex-1 min-h-0 bg-white/10"
      : "grid grid-rows-2 gap-px flex-1 min-h-0 bg-white/10";

  return (
    <div
      className="h-dvh w-screen flex flex-col overflow-hidden bg-[#0A0A0A] text-white"
      data-testid="video-compare-app"
    >
      <TopBar
        layout={layout}
        setLayout={setLayout}
        syncEnabled={syncEnabled}
        setSyncEnabled={setSyncEnabled}
        onSwap={swapVideos}
        canSwap={!!sourceA || !!sourceB}
        onShowAnalytics={onShowAnalytics}
        onStartExport={startExport}
      />

      {isRecording && (
        <ExportModal
          layout={layout}
          panelARef={panelARef}
          panelBRef={panelBRef}
          onClose={stopExport}
        />
      )}

      <div className={videoWrapperClass} data-testid="video-grid">
        <VideoPanel
          ref={panelARef}
          label="A"
          source={sourceA}
          setSource={setSourceA}
          syncEnabled={syncEnabled}
          globalRate={rate}
          layout={layout}
          onPlayChange={(p) => onLocalPlayChange("A", p)}
          onSeek={(t) => onLocalSeek("A", t)}
          data-testid="panel-A"
        />
        <VideoPanel
          ref={panelBRef}
          label="B"
          source={sourceB}
          setSource={setSourceB}
          syncEnabled={syncEnabled}
          globalRate={rate}
          layout={layout}
          onPlayChange={(p) => onLocalPlayChange("B", p)}
          onSeek={(t) => onLocalSeek("B", t)}
          data-testid="panel-B"
        />
      </div>

      <ControlBar
        globalPlaying={globalPlaying}
        onTogglePlay={toggleGlobalPlay}
        onStepBack={() => stepFrames(-1)}
        onStepForward={() => stepFrames(1)}
        onReset={resetAll}
        rate={rate}
        onRateChange={applyRate}
        syncEnabled={syncEnabled}
      />
    </div>
  );
}
